import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'

interface Patient {
  id: string
  patient_id: string
  full_name: string
  date_of_birth: string
  gender: string
  phone: string
  created_at: string
}

interface PatientWithSlides extends Patient {
  slide_count: number
}

const ReceptionTab: React.FC = () => {
  const [patients, setPatients] = useState<PatientWithSlides[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    fetchPatientsForReception()
  }, [])

  const fetchPatientsForReception = async () => {
    setLoading(true)
    try {
      // Fetch patients and slides
      const [patientsRes, slidesRes] = await Promise.all([
        fetch('/api/v1/pathology/patients/?page_size=1000'),
        fetch('/api/v1/imaging/slides/?page_size=1000')
      ])

      if (!patientsRes.ok) {
        throw new Error('Failed to fetch patients')
      }

      const [patientsData, slidesData] = await Promise.all([
        patientsRes.json(),
        slidesRes.ok ? slidesRes.json() : Promise.resolve({ results: [] })
      ])

      const allPatients = patientsData.results || patientsData
      const slides = (slidesData.results || slidesData) || []

      // Count slides per patient
      const slidesByPatient = new Map<string, number>()
      slides.forEach((slide: any) => {
        if (slide.patient_id) {
          const count = slidesByPatient.get(slide.patient_id.toString()) || 0
          slidesByPatient.set(slide.patient_id.toString(), count + 1)
        }
      })

      // Filter patients without slides (new reception)
      const patientsWithoutSlides = allPatients
        .filter((p: Patient) => !slidesByPatient.has(p.id.toString()))
        .map((p: Patient) => ({
          ...p,
          slide_count: 0
        }))
        .sort((a: Patient, b: Patient) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )

      setPatients(patientsWithoutSlides)
    } catch (error) {
      console.error('Error fetching reception data:', error)
      toast.error('Không thể tải dữ liệu tiếp nhận')
    } finally {
      setLoading(false)
    }
  }

  const filteredPatients = patients.filter(p =>
    p.patient_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.phone.includes(searchTerm)
  )

  return (
    <div>
      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Tìm theo mã BN, họ tên, số điện thoại..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Stats */}
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Tổng số: <span className="font-semibold text-gray-900">{filteredPatients.length}</span> bệnh nhân cần upload ảnh
        </div>
        <button
          onClick={fetchPatientsForReception}
          className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100"
        >
          🔄 Làm mới
        </button>
      </div>

      {/* Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mã BN
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Họ và Tên
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ngày sinh
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Giới tính
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Số điện thoại
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ngày tiếp nhận
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Thao tác
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  Đang tải dữ liệu...
                </td>
              </tr>
            ) : filteredPatients.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  {searchTerm ? 'Không tìm thấy bệnh nhân' : 'Tất cả bệnh nhân đã có ảnh 🎉'}
                </td>
              </tr>
            ) : (
              filteredPatients.map((patient) => (
                <tr key={patient.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {patient.patient_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {patient.full_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(patient.date_of_birth).toLocaleDateString('vi-VN')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {patient.gender}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {patient.phone}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(patient.created_at).toLocaleDateString('vi-VN')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <Link
                      to={`/imaging/upload?patient_id=${patient.id}`}
                      className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                    >
                      📤 Upload ảnh
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default ReceptionTab
